import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from './store'
import { setPermissions, setLoading, setError, clearAdmin } from './store/slices/AdminSlice'
import { getPermissions } from './services/adminApi'
import { getSession, onAuthStateChange } from './services/auth'
import { PageLayout } from './components/layout/PageLayout'
import { Dashboard } from './pages/Dashboard'
import { AccessDenied } from './pages/AccessDenied'
import { Login } from './pages/Login'
import { Listings } from './pages/Listings'
import { Users } from './pages/Users'
import { Shops } from './pages/Shops'
import { Applications } from './pages/Applications'
import { Campaigns } from './pages/Campaigns'
import { Programs } from './pages/Programs'
import { Reports } from './pages/Reports'
import { Settings } from './pages/Settings'
import { Invites } from './pages/Invites'
import { Events } from './pages/Events'
import { EventCities } from './pages/EventCities'
import { HostedEvents } from './pages/HostedEvents'
import { HostedEventDetail } from './pages/HostedEvents/HostedEventDetail'
import { Rebates } from './pages/Rebates'
import { FeaturedShops } from './pages/FeaturedShops'
import { Announcements } from './pages/Announcements'
import { Notifications } from './pages/Notifications'
import { Orders } from './pages/Orders'
import { Memberships } from './pages/Memberships'
import { Integrations } from './pages/Integrations'
import { BackstageModeration } from './pages/BackstageModeration'
import { Support } from './pages/Support'
import { TrustQueue } from './pages/Trust'
import { TrustDecisionDetail } from './pages/Trust/TrustDecisionDetail'
import { TrustMetrics } from './pages/Trust/TrustMetrics'
import type { AdminRole } from './types/AdminTypes'

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

function App() {
  const dispatch = useDispatch()
  const { isLoading, error, role } = useSelector((state: RootState) => state.admin)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  // Check auth session on mount
  useEffect(() => {
    async function checkAuth() {
      const { session } = await getSession()
      setIsAuthenticated(!!session)
    }
    checkAuth()

    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        setIsAuthenticated(true)
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false)
        dispatch(clearAdmin())
      }
    })

    return () => subscription.unsubscribe()
  }, [dispatch])

  // Load permissions when authenticated
  useEffect(() => {
    if (!isAuthenticated) return

    async function loadPermissions() {
      dispatch(setLoading(true))
      try {
        const response = await getPermissions()
        if (response.ok && response.data) {
          dispatch(
            setPermissions({
              userId: response.data.user_id,
              email: response.data.email,
              role: response.data.role as AdminRole,
              permissions: response.data.permissions,
            })
          )
        } else {
          dispatch(setError(response.error?.message || 'Failed to load permissions'))
        }
      } catch {
        dispatch(setError('Failed to connect to admin API'))
      }
    }

    loadPermissions()
  }, [dispatch, isAuthenticated])

  // Still checking auth
  if (isAuthenticated === null || (isAuthenticated && isLoading)) {
    return <Loading />
  }

  // Not logged in - show login page
  if (!isAuthenticated) {
    return <Login />
  }

  // Logged in but no admin role
  if (error || !role) {
    return <AccessDenied />
  }

  return (
    <PageLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/users" element={<Users />} />
        <Route path="/shops" element={<Shops />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/events" element={<Events />} />
        <Route path="/event-cities" element={<EventCities />} />
        <Route path="/hosted-events" element={<HostedEvents />} />
        <Route path="/hosted-events/:marketId" element={<HostedEventDetail />} />
        <Route path="/rebates" element={<Rebates />} />
        <Route path="/featured-shops" element={<FeaturedShops />} />
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/memberships" element={<Memberships />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/backstage-moderation" element={<BackstageModeration />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/support" element={<Support />} />
        <Route path="/invites" element={<Invites />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/trust" element={<TrustQueue />} />
        <Route path="/trust/decisions/:decisionId" element={<TrustDecisionDetail />} />
        <Route path="/trust/metrics" element={<TrustMetrics />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PageLayout>
  )
}

export default App
